import { extensionApiRef } from '@agilelab/plugin-wb-builder-catalog';
import { EntityErrorResult } from '@agilelab/plugin-wb-catalog-extension-common';
import {
  ALL_TAXONOMIES_FILTER,
  PracticeShaperFilters,
  TaxonomyBlueprintFilter,
  useTaxonomySelection,
} from '@agilelab/plugin-wb-practice-shaper';
import { Entity } from '@mainstage/catalog-model';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import {
  DefaultEntityFilters,
  EntityKindFilter,
  useEntityList,
} from '@mainstage/plugin-catalog-react';
import { TemplateEntityV1beta3 } from '@mainstage/plugin-scaffolder-common';
import {
  ComponentType,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useEffect,
  useState,
} from 'react';
import useAsync from 'react-use/lib/useAsync';

export type ViewType = 'table' | 'grid';

export interface BlueprintListContextType {
  viewType: ViewType;
  setViewType: Dispatch<SetStateAction<ViewType>>;

  entities: Entity[];
  loading: boolean;
  error: Error | undefined;
  filters: DefaultEntityFilters;
  updateFilters: (
    filters:
      | Partial<DefaultEntityFilters>
      | ((prevFilters: DefaultEntityFilters) => Partial<DefaultEntityFilters>),
  ) => void;

  TemplateCardComponent?:
    | ComponentType<{ template: TemplateEntityV1beta3 }>
    | undefined;

  errors: EntityErrorResult[] | undefined;
}

export const BlueprintListContext = createContext<BlueprintListContextType>(
  {} as BlueprintListContextType,
);

interface Props {
  children?: ReactNode;
  TemplateCardComponent?:
    | ComponentType<{ template: TemplateEntityV1beta3 }>
    | undefined;
}

export const BlueprintListContextProvider: FC<Props> = ({
  children,
  TemplateCardComponent,
}) => {
  const catalogApi = useApi(extensionApiRef);
  const identityApi = useApi(identityApiRef);
  const [viewType, setViewType] = useState<ViewType>('grid');
  const { selectedTaxonomyRef } = useTaxonomySelection();
  const { entities, updateFilters, filters, loading, error } = useEntityList();

  useEffect(() => {
    const blueprintFilters: Partial<PracticeShaperFilters> = {
      kind: new EntityKindFilter('blueprint', 'blueprint'),
    };
    blueprintFilters.taxonomy = new TaxonomyBlueprintFilter(
      selectedTaxonomyRef !== ALL_TAXONOMIES_FILTER
        ? [selectedTaxonomyRef]
        : [],
    );
    updateFilters(blueprintFilters);
  }, [updateFilters, selectedTaxonomyRef]);

  const { value: errors } = useAsync(async () => {
    try {
      const entityIds = entities.flatMap(e =>
        e.metadata.uid ? [e.metadata.uid] : [],
      );
      const { token } = await identityApi.getCredentials();
      const result = await catalogApi.getEntityErrors(entityIds, { token });
      return result ?? [];
    } catch (e) {
      return [];
    }
  }, []);

  return (
    <BlueprintListContext.Provider
      value={{
        viewType,
        setViewType,

        entities,
        loading,
        filters,
        updateFilters,
        error,

        TemplateCardComponent,
        errors,
      }}
    >
      {children}
    </BlueprintListContext.Provider>
  );
};
